import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Eye, EyeOff, Loader2, X, Shield, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

export default function LoginModal({ isOpen, onClose, defaultTab = 'staff' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [staffForm, setStaffForm] = useState({ email: '', password: '' });
  const [adherentForm, setAdherentForm] = useState({ email: '', password: '', first_name: '', last_name: '', phone: '' });
  const [isRegistering, setIsRegistering] = useState(false);

  const handleStaffLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, staffForm);
      localStorage.setItem('briouso_token', res.data.token);
      localStorage.setItem('briouso_user', JSON.stringify(res.data.user));
      toast.success(`${t('common.welcome')}, ${res.data.user.first_name}!`);
      onClose();
      if (res.data.user.role === 'admin' || res.data.user.role === 'collaborateur') {
        navigate('/admin');
      } else {
        navigate('/portail-adherent');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdherentLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegistering) {
        await axios.post(`${API}/auth/register`, { ...adherentForm, role: 'adherent' });
        toast.success(t('loginModal.accountCreated'));
        setIsRegistering(false);
        setAdherentForm({ ...adherentForm, first_name: '', last_name: '', phone: '' });
      } else {
        const res = await axios.post(`${API}/auth/login`, {
          email: adherentForm.email,
          password: adherentForm.password
        });
        localStorage.setItem('briouso_token', res.data.token);
        localStorage.setItem('briouso_user', JSON.stringify(res.data.user));
        toast.success(`${t('common.welcome')}, ${res.data.user.first_name}!`);
        onClose();
        navigate('/portail-adherent');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="login-modal"
    >
      <Card className="w-full max-w-md border-0 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="relative">
          <Button variant="ghost" size="icon" className="absolute right-4 top-4" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
          <div className="flex justify-center mb-4">
            {activeTab === 'staff' ? (
              <Shield className="h-12 w-12 text-fuchsia-600" />
            ) : (
              <User className="h-12 w-12 text-lime-600" />
            )}
          </div>
          <CardTitle className="text-center text-2xl">
            {activeTab === 'staff' ? t('loginModal.staffArea') : t('loginModal.memberArea')}
          </CardTitle>
          <CardDescription className="text-center">
            {activeTab === 'staff' 
              ? t('loginModal.staffDesc')
              : isRegistering ? t('loginModal.createAccount') : t('loginModal.memberDesc')
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-6">
            <Button
              type="button"
              variant={activeTab === 'staff' ? 'default' : 'outline'}
              className={`flex-1 ${activeTab === 'staff' ? 'bg-fuchsia-600 hover:bg-fuchsia-700' : ''}`}
              onClick={() => setActiveTab('staff')}
            >
              <Shield className="h-4 w-4 mr-2" />
              Staff
            </Button>
            <Button
              type="button"
              variant={activeTab === 'adherent' ? 'default' : 'outline'}
              className={`flex-1 ${activeTab === 'adherent' ? 'bg-lime-600 hover:bg-lime-700' : ''}`}
              onClick={() => setActiveTab('adherent')}
            >
              <User className="h-4 w-4 mr-2" />
              {t('loginModal.member')}
            </Button>
          </div>

          {activeTab === 'staff' && (
            <form onSubmit={handleStaffLogin} className="space-y-4">
              <div>
                <Label>{t('common.email')}</Label>
                <Input type="email" value={staffForm.email} onChange={(e) => setStaffForm({...staffForm, email: e.target.value})} placeholder="admin@briouso-folies.fr" required data-testid="staff-email" />
              </div>
              <div>
                <Label>{t('common.password')}</Label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} value={staffForm.password} onChange={(e) => setStaffForm({...staffForm, password: e.target.value})} placeholder="••••••••" required data-testid="staff-password" />
                  <Button type="button" variant="ghost" size="icon" className="absolute right-2 top-1/2 -translate-y-1/2" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="staff-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4 mr-2" />{t('loginModal.signIn')}</>}
              </Button>
            </form>
          )}

          {activeTab === 'adherent' && (
            <form onSubmit={handleAdherentLogin} className="space-y-4">
              {isRegistering && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('loginModal.firstName')}</Label>
                      <Input type="text" value={adherentForm.first_name} onChange={(e) => setAdherentForm({...adherentForm, first_name: e.target.value})} required data-testid="adherent-firstname" />
                    </div>
                    <div>
                      <Label>{t('loginModal.lastName')}</Label>
                      <Input type="text" value={adherentForm.last_name} onChange={(e) => setAdherentForm({...adherentForm, last_name: e.target.value})} required data-testid="adherent-lastname" />
                    </div>
                  </div>
                  <div>
                    <Label>{t('loginModal.phone')}</Label>
                    <Input type="tel" value={adherentForm.phone} onChange={(e) => setAdherentForm({...adherentForm, phone: e.target.value})} data-testid="adherent-phone" />
                  </div>
                </>
              )}
              <div>
                <Label>{t('common.email')}</Label>
                <Input type="email" value={adherentForm.email} onChange={(e) => setAdherentForm({...adherentForm, email: e.target.value})} placeholder="vous@email.com" required data-testid="adherent-email" />
              </div>
              <div>
                <Label>{t('common.password')}</Label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} value={adherentForm.password} onChange={(e) => setAdherentForm({...adherentForm, password: e.target.value})} placeholder="••••••••" required data-testid="adherent-password" />
                  <Button type="button" variant="ghost" size="icon" className="absolute right-2 top-1/2 -translate-y-1/2" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-lime-600 hover:bg-lime-700" data-testid="adherent-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : isRegistering ? t('loginModal.createMyAccount') : t('loginModal.signIn')}
              </Button>
              <Button type="button" variant="link" className="w-full text-zinc-600" onClick={() => setIsRegistering(!isRegistering)}>
                {isRegistering ? t('loginModal.alreadyAccount') : t('loginModal.noAccount')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
